import React, { useState, useMemo, useEffect } from "react";
import { detectDataFormat } from '../../utils/chartUtils';
import { 
  ChartHeader, 
  TimeSeriesChartSelector, 
  SankeyChartSelector, 
  KPIChartSelector 
} from './CommonComponents';
import { SankeyChart } from './SankeyComponents';
import HeatmapTable from './HeatmapTable';
import { VelocityPivotChart, VelocityBucketChart, VelocityPercentileChart } from './VelocityComponents';
import { TimeSeriesChart } from './TimeSeriesComponents';
import { KPIVelocityChart } from './KPIComponents';

export default function Charts({
  data,
  chartType: propChartType,
  showControls = true,
  title,
  subtitle,
  height = 400,
  compact = false,
  columnMapping,
  availableUserTypes,
  onDataFiltered
}) {

  const [internalChartType, setInternalChartType] = useState("stacked");
  const [sankeySortBy, setSankeySortBy] = useState("name");
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [sankeyViewMode, setSankeyViewMode] = useState("flow");
  const [activeUserFilter, setActiveUserFilter] = useState("All");
  
  const chartType = propChartType || internalChartType;

  const dataFormat = data && data.length ? detectDataFormat(data) : 'unknown';

  const displayData = useMemo(() => {
    if (!data || !data.length) return [];
    
    let processedData = data;
    
    if (dataFormat === 'sankey' && activeUserFilter !== 'All') {
      processedData = data.reduce((acc, item) => {
        let val = 0;
        let isMatch = false;
        const searchTarget = activeUserFilter.toLowerCase(); 
        
        for (const [key, value] of Object.entries(item)) {
          if (key.toLowerCase().includes(searchTarget) && typeof value === 'number') {
            val = value;
            isMatch = true;
            break; 
          }
        }
        
        if (!isMatch) {
          for (const [key, value] of Object.entries(item)) {
            if (typeof value === 'string' && value.toLowerCase() === searchTarget) {
              val = Number(item.value) || Number(item.veh_count) || Number(item.count) || Number(item.total) || 0;
              isMatch = true;
              break;
            }
          }
        }
          
        if (isMatch && val > 0) {
          acc.push({ ...item, value: val });
        }
        return acc;
      }, []);
    }
    
    return processedData;
  }, [data, dataFormat, activeUserFilter]);

  useEffect(() => {
    if (onDataFiltered) {
      onDataFiltered(displayData);
    }
  }, [displayData, onDataFiltered]);


  if (!data || !data.length) {
    return (
      <div className={`flex items-center justify-center ${compact ? 'h-full' : 'h-64'} bg-gray-50 rounded`}>
        <p className="text-gray-500 text-sm">No data available</p>
      </div>
    );
  }


  const chartMargin = compact
    ? { top: 10, right: 10, bottom: 10, left: 10 }
    : dataFormat === 'sankey' 
      ? { top: 20, right: 220, left: 10, bottom: 20 }
      : { top: 20, right: 30, left: 20, bottom: 20 };

  const responsiveProps = {
    width: "100%",
    height: compact ? '100%' : height
  };

  const xAxisProps = {
    tick: { fontSize: compact ? 10 : 12 },
    angle: compact ? -45 : 0,
    textAnchor: compact ? "end" : "middle",
    height: compact ? 60 : undefined
  };

  const yAxisProps = {
    tick: { fontSize: compact ? 10 : 12 }
  };

  const tooltipProps = {
    contentStyle: {
      fontSize: compact ? 11 : 13,
      padding: compact ? '4px 8px' : '8px 12px'
    }
  };

  const legendProps = {
    wrapperStyle: {
      fontSize: compact ? 10 : 12,
      paddingTop: compact ? '5px' : '10px'
    }
  };

  const getDefaultTitle = () => {
    if (title) return title;
    switch (dataFormat) {
      case 'velocity-pivot': return "Velocity Distribution Analysis";
      case 'time-series': return "Time Series Analysis";
      case 'velocity-bucket': return "Velocity Distribution";
      case 'kpi-velocity': return "Speed Distribution";
      case 'sankey': return "Flow Diagram";
      case 'velocity-percentile': return "Speed Percentiles Over Time";
      default: return "Data Visualization";
    }
  };

  const renderChart = () => {
    // Safely handle cases where there are strictly 0 movements for the selected filter
    if (dataFormat === 'sankey' && displayData.length === 0 && activeUserFilter !== 'All') {
      return (
        <div className={`flex items-center justify-center ${compact ? 'h-full' : 'h-64'} bg-gray-50 rounded`}>
          <p className="text-gray-500 text-sm">
            No {activeUserFilter.toLowerCase()} movements recorded for these filters.
          </p>
        </div>
      );
    }

    const commonProps = {
      data: displayData,
      chartType,
      compact,
      height,
      chartMargin,
      responsiveProps,
      xAxisProps,
      yAxisProps,
      tooltipProps,
      legendProps,
      columnMapping
    };

    switch (dataFormat) {
      case 'sankey':
        if (sankeyViewMode === 'table') {
          return (
            <HeatmapTable
              data={displayData}
              title={!compact ? title || "Flow Heatmap" : undefined}
              subtitle={!compact ? subtitle : undefined}
              showLegend={!compact}
              showValues={true}
              compact={compact}
              columnMapping={columnMapping}
            />
          );
        }
        return <SankeyChart {...commonProps} sortBy={sankeySortBy} showHeatmap={showHeatmap} />;
        
      case 'velocity-pivot':
        return <VelocityPivotChart {...commonProps} />;
      
      case 'time-series':
        return <TimeSeriesChart {...commonProps} activeUserFilter={activeUserFilter} />;
      
      case 'velocity-bucket':
        return <VelocityBucketChart {...commonProps} />;
      
      case 'velocity-percentile':                                 
        return <VelocityPercentileChart {...commonProps} />;      
      
      case 'kpi-velocity':
        return <KPIVelocityChart {...commonProps} />;
      
      default:
        return (
          <div className="flex items-center justify-center h-64 bg-gray-50 rounded">
            <p className="text-gray-500 text-sm">Unsupported data format</p>
          </div>
        );
    }
  };

  const renderChartSelector = () => {
    if (!showControls || compact) return null;

    if (dataFormat === 'velocity-percentile') return null;

    if (dataFormat === 'sankey') {
      return (
        <SankeyChartSelector 
          sortBy={sankeySortBy} 
          onChange={setSankeySortBy}
          showHeatmap={showHeatmap}
          onHeatmapToggle={setShowHeatmap}
          viewMode={sankeyViewMode}
          onViewModeChange={setSankeyViewMode}
          data={data}
          activeUserFilter={activeUserFilter}      
          onUserFilterChange={setActiveUserFilter}
          availableUserTypes={availableUserTypes}
        />
      );
    }

    if (dataFormat === 'kpi-velocity') {
      return <KPIChartSelector chartType={chartType} onChange={setInternalChartType} />;
    }
    
    return <TimeSeriesChartSelector chartType={chartType} onChange={setInternalChartType} />;
  };

  return (
    <div className={compact ? "h-full w-full" : ""}>
      <div className="h-full">
        {renderChartSelector()}
        
        {!compact && (
          <ChartHeader 
            title={getDefaultTitle()} 
            subtitle={subtitle} 
          />
        )}

        {renderChart()}
      </div>
    </div>
  );
}